import { sinkListItem, liftListItem } from "prosemirror-schema-list";
import {
    autoJoin,
    chainCommands,
    lift,
    joinBackward,
    joinTextblockBackward,
} from "prosemirror-commands";
import { NodeType, ResolvedPos, Schema } from "prosemirror-model";
import { EditorView } from "prosemirror-view";
import { Node } from "prosemirror-model";
import {
    Command,
    EditorState,
    NodeSelection,
    TextSelection,
} from "prosemirror-state";

const TAB = "    ";

const getNearestListNode = (currentState: EditorState) => {
    const { $from, to } = currentState.selection;
    const same = $from.sharedDepth(to);
    for (let depth = same; depth !== 0; depth -= 1) {
        const pos = $from.before(depth);
        const nodeSelection = NodeSelection.create(currentState.doc, pos);
        if (
            nodeSelection.node.type.name === "bullet_list" ||
            nodeSelection.node.type.name === "ordered_list"
        ) {
            return nodeSelection;
        }
    }
    return null;
};

const convertToListType: (listType: NodeType) => Command =
    (listType: NodeType) => (state, dispatch, view) => {
        const nearestListSelection = getNearestListNode(state);
        if (
            !nearestListSelection ||
            nearestListSelection.node.type === listType
        ) {
            return false;
        }
        if (view && dispatch) {
            const tr = view.state.tr.setNodeMarkup(
                nearestListSelection.from,
                listType
            );
            dispatch(tr);
        }
        return true;
    };

const getEncompassingNodes = (
    state: EditorState
): {
    from: number;
    to: number;
} => {
    const { from, to } = state.selection;

    // Adjust selection to encompass complete s
    const $from = state.doc.resolve(from);
    const $to = state.doc.resolve(to);
    return {
        from: $from.depth === 0 ? from : $from.before($from.depth),
        to: $to.depth === 0 ? to : $to.after($to.depth),
    };
};

/**
 * Add additional indentation for a selection inside a code block.
 */
const indentMore: Command = (state, dispatch) => {
    if (state.selection.empty) {
        return false;
    }

    let { tr } = state;
    const { from, to } = getEncompassingNodes(state);
    const indentedNodes: Node[] = [];

    const fragment = state.doc.slice(from, to);
    // Only works for code blocks
    if (fragment.content.child(0).type !== state.schema.nodes.code_block) {
        return false;
    }

    fragment.content.forEach((node) => {
        if (node.isText) {
            // For text nodes, preserve marks while adding indentation
            indentedNodes.push(state.schema.text(TAB + node.text, node.marks));
        } else {
            // For non-text nodes (like paragraphs, etc), preserve them and indent their text content
            const newContent: Node[] = [];
            node.descendants((childNode) => {
                if (childNode.isText) {
                    newContent.push(
                        state.schema.text(TAB + childNode.text, childNode.marks)
                    );
                } else {
                    newContent.push(childNode.copy(childNode.content));
                }
                return true;
            });
            // Create a new node of the same type with indented content
            indentedNodes.push(
                node.type.create(node.attrs, newContent, node.marks)
            );
        }
    });

    // Calculate relative positions within the selected block
    const relativeFrom = state.selection.from - from;
    const relativeTo = state.selection.to - from;

    tr = tr.replaceWith(from, to, indentedNodes);

    // Calculate new absolute positions, accounting for added indentation
    const newFrom = from + relativeFrom + TAB.length;
    const newTo = from + relativeTo + TAB.length;

    // Restore the selection at the adjusted positions
    tr = tr.setSelection(TextSelection.create(tr.doc, newFrom, newTo));

    if (dispatch) {
        dispatch(tr);
        return true;
    }
    return false;
};

/**
 * Remove any indentation for selected lines.
 */
const indentLess: Command = (state, dispatch) => {
    let { tr } = state;
    const { from, to } = getEncompassingNodes(state);
    const indentedNodes: Node[] = [];

    const dedentRegex = /^\s{1,4}/;
    // Number of characters changed. Should be negative if anything has changed.
    let changed = 0;

    state.doc.slice(from, to).content.forEach((node) => {
        if (node.isText) {
            // For text nodes, preserve marks while adding indentation
            const newString = node.text!.replace(dedentRegex, "");
            changed += newString.length - node.text!.length;
            indentedNodes.push(state.schema.text(newString, node.marks));
        } else {
            // For non-text nodes (like paragraphs, etc), preserve them and indent their text content
            const newContent: Node[] = [];
            node.descendants((childNode) => {
                if (childNode.isText) {
                    const newString = childNode.text!.replace(dedentRegex, "");
                    changed += newString.length - childNode.text!.length;
                    newContent.push(
                        state.schema.text(newString, childNode.marks)
                    );
                } else {
                    newContent.push(childNode.copy(childNode.content));
                }
                return true;
            });
            // Create a new node of the same type with indented content
            indentedNodes.push(
                node.type.create(node.attrs, newContent, node.marks)
            );
        }
    });

    if (changed < 0) {
        tr = tr.replaceWith(from, to, indentedNodes);

        // Restore the selection at the adjusted positions
        tr = tr.setSelection(
            TextSelection.create(
                tr.doc,
                from === state.selection.from
                    ? state.selection.from
                    : state.selection.from + changed,
                state.selection.to + changed
            )
        );
        if (dispatch) {
            dispatch(tr);
            return true;
        }
    }
    return false;
};

const indent = (schema: Schema) =>
    autoJoin(
        chainCommands(
            sinkListItem(schema.nodes.list_item),
            indentMore,
            // Insert a TAB.
            (state, dispatch) => {
                const { from, to, empty } = state.selection;
                if (!empty) {
                    return false;
                }
                const tr = state.tr.insertText(TAB, from, to);
                if (dispatch) {
                    dispatch(tr);
                    return true;
                }
                return false;
            },
            () => true
        ),
        (before, after) => {
            return (
                ["ordered_list", "bullet_list"].includes(before.type.name) &&
                ["ordered_list", "bullet_list"].includes(after.type.name)
            );
        }
    );

const joinBackwardUnlessAtDocStart: Command = (state, dispatch, view) => {
    if (state.selection.$head.depth <= 1) {
        // We are at the left margin, so performing a backward join here
        // would move the cursor to the previous block
        return false;
    }
    return joinBackward(state, dispatch, view);
};

const unindent = (schema: Schema) =>
    chainCommands(
        liftListItem(schema.nodes.list_item),
        indentLess,
        joinBackwardUnlessAtDocStart,
        lift
    );

function atBlockStart(
    state: EditorState,
    view?: EditorView
): ResolvedPos | null {
    const { $cursor } = state.selection as TextSelection;
    if (
        !$cursor ||
        (view
            ? !view.endOfTextblock("backward", state)
            : $cursor.parentOffset > 0)
    )
        return null;
    return $cursor;
}

const liftListItemIfAtStart: (listItemType: NodeType) => Command =
    (listItemType) => (state, dispatch, view) => {
        if (!atBlockStart(state, view)) {
            return false;
        }
        // Check that we are at the start
        return liftListItem(listItemType)(state, dispatch, view);
    };

const joinTopLevelWithPreviousListItem: Command = (state, dispatch, view) => {
    if (!atBlockStart(state, view)) {
        return false;
    }
    if (state.selection.$from.depth !== 1) {
        return false;
    }
    return joinTextblockBackward(state, dispatch, view);
};

export {
    convertToListType,
    indent,
    unindent,
    getNearestListNode,
    liftListItemIfAtStart,
    joinTopLevelWithPreviousListItem,
};
